import { type Conversation } from "shared/apiClient";
import { cn, getText, interpolate } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

export interface ReportWarningPanelProps {
  conversation: Conversation | undefined | null;
  headingClassName?: string;
}

export function ReportWarningPanel({
  conversation,
  headingClassName,
}: ReportWarningPanelProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3 className={cn("text-textPrimary text-headingsm", headingClassName)}>
          {getText(
            "This analysis does not represent an official planning decision",
          )}
        </h3>

        <p className="text-textPrimary text-bodymd">
          {getText(
            "Artificial Intelligence (AI) was used to analyse your proposal and it can sometimes make mistakes. It also doesn't account for a few things, such as:",
          )}
        </p>

        <ul className="text-textPrimary text-bodymd flex list-outside list-disc flex-col gap-1 pl-5">
          <li>
            {interpolate(
              "covenants, 173 agreements and other restrictions found on a {{link}}",
              {
                link: (
                  <a
                    className="text-textLink underline"
                    href={getLocalisationEntry(
                      "councilApplyForCertificateOfTitleLink",
                    )}
                    rel="noopener"
                    target="_blank"
                    tabIndex={0}
                  >
                    {getText("Certificate of Title")}
                  </a>
                ),
              },
            )}
          </li>
          <li>
            {getText(
              "factors relating to pre-existing agreements such as existing use rights, past planning approvals, and existing permit applications",
            )}
          </li>
          <li>
            {getText(
              "other approvals that may be required, such as building and asset protection permits.",
            )}
          </li>
        </ul>

        <p className="text-textPrimary text-bodymd">
          {interpolate("This is not a comprehensive list. {{link}}.", {
            link: (
              <a
                className="text-textLink underline"
                href={`/analysis-exclusions?state=${conversation?.address_data?.state}`}
                rel="noopener"
                target="_blank"
                tabIndex={0}
              >
                {getText("See what else this analysis doesn't account for")}
              </a>
            ),
          })}
        </p>

        <p className="text-textPrimary text-bodymd">
          {interpolate(
            "The information provided by this service is intended as a helpful guide and is not a planning decision. {{contactCouncilLink}} to talk to a planner if you're unsure about anything.",
            {
              contactCouncilLink: (
                <a
                  className="text-textLink underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noopener"
                  target="_blank"
                  tabIndex={0}
                >
                  {getText("Contact council")}
                </a>
              ),
            },
          )}
        </p>

        <p className="text-textPrimary text-bodymd">
          {interpolate(
            "If you require written confirmation of whether a planning permit is required or not, this report cannot be used for that. You must {{link}}.",
            {
              link: (
                <a
                  className="text-textLink underline"
                  href={getLocalisationEntry(
                    "councilWrittenPlanningAdviceLink",
                  )}
                  rel="noopener"
                  target="_blank"
                  tabIndex={0}
                >
                  {getText("apply for written planning advice")}
                </a>
              ),
            },
          )}
        </p>

        <p className="text-textPrimary text-bodymd">
          {getText(
            "Acting without necessary permits may lead to enforcement action. Council and myLot will not be responsible for any loss arising from use of or reliance on this AI-generated analysis.",
          )}
        </p>
      </div>
    </div>
  );
}
